import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["treatmentSelect", "dateInput", "startsAt", "inRoom", "sourceSelect", "foName", "foHint", "therapistSelect", "tbaHint", "slotError"]
  static values = { bookingId: Number }

  connect() {
    this.syncTbaMode()

    if (this.treatmentSelectTarget.value && this.dateInputTarget.value) {
      this.loadSlots()
    }

    this.syncFoNameRequirement()
  }

  disconnect() {
    this.slotRequestController?.abort()
    this.slotRequestController = null
  }

  treatmentChanged() { this.loadSlots() }
  dateChanged() { this.loadSlots() }
  scheduleChanged() {
    this.startsAtTarget.value = ""
    this.clearSlotHighlight()
    this.loadSlots()
  }
  toggleInRoom() { this.loadSlots() }
  sourceChanged() { this.syncFoNameRequirement() }

  // Changing the therapist filters availability — clear any chosen slot first.
  therapistChanged() {
    this.startsAtTarget.value = ""
    this.clearSlotHighlight()
    this.syncTbaMode()
    this.loadSlots()
  }

  // "TBA" (To Be Assigned) unlocks the far-future booking window and shows a
  // hint, since an unassigned slot isn't bound to any therapist's roster.
  syncTbaMode() {
    if (!this.hasTherapistSelectTarget) return
    const tba = this.therapistSelectTarget.value === "tba"

    const max = tba ? this.dateInputTarget.dataset.maxUnassigned : this.dateInputTarget.dataset.maxDefault
    if (max) this.dateInputTarget.max = max

    if (this.hasTbaHintTarget) this.tbaHintTarget.classList.toggle("hidden", !tba)
  }

  // Called when Zenna AI fills the starts_at hidden field — extract the date
  // and populate the date input so the slot picker loads correctly
  startsAtChanged() {
    const startsAt = this.startsAtTarget.value
    if (!startsAt) return

    const date = startsAt.substring(0, 10) // "YYYY-MM-DD"
    if (!date) return

    if (this.dateInputTarget.value !== date) {
      this.dateInputTarget.value = date
    }

    if (this.treatmentSelectTarget.value) {
      this.loadSlots()
    }
  }

  syncFoNameRequirement() {
    if (!this.hasSourceSelectTarget || !this.hasFoNameTarget) return

    const hotelSource = this.sourceSelectTarget.value === "hotel"
    this.foNameTarget.required = hotelSource
    this.foNameTarget.setAttribute("aria-required", hotelSource ? "true" : "false")

    if (this.hasFoHintTarget) {
      this.foHintTarget.textContent = hotelSource ? "Wajib untuk sumber Hotel." : "Opsional kecuali sumber Hotel."
      this.foHintTarget.classList.toggle("text-error", hotelSource)
      this.foHintTarget.classList.toggle("font-medium", hotelSource)
      this.foHintTarget.classList.toggle("text-olive", !hotelSource)
    }
  }

  async loadSlots() {
    // A later filter change always supersedes an earlier availability request,
    // including when the latest values are incomplete and no request is sent.
    this.slotRequestController?.abort()
    this.slotRequestController = null

    const treatmentId = this.treatmentSelectTarget.value
    const date = this.dateInputTarget.value
    if (!treatmentId || !date) return

    const frame = document.getElementById("dashboard-slots")
    if (!frame) return

    let url = `/dashboard/bookings/slots?treatment_id=${treatmentId}&date=${date}`
    if (this.hasBookingIdValue) {
      url += `&booking_id=${this.bookingIdValue}`
    }
    if (this.hasInRoomTarget && this.inRoomTarget.checked) {
      url += "&in_room=1"
    }
    if (this.hasTherapistSelectTarget && this.therapistSelectTarget.value) {
      url += `&therapist_id=${this.therapistSelectTarget.value}`
    }

    const requestController = new AbortController()
    this.slotRequestController = requestController

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" },
        signal: requestController.signal
      })
      if (!response.ok) return
      const html = await response.text()
      if (requestController !== this.slotRequestController) return

      const parser = new DOMParser()
      const doc = parser.parseFromString(html, "text/html")
      const newFrame = doc.querySelector("turbo-frame#dashboard-slots")
      if (newFrame) {
        frame.innerHTML = newFrame.innerHTML

        // If starts_at is already set (e.g. via Zenna), highlight the matching slot
        const startsAt = this.startsAtTarget.value
        if (startsAt) {
          const match = frame.querySelector(`[data-starts-at="${startsAt}"]`)
          if (match) {
            match.classList.add("bg-primary/10", "border-primary", "font-semibold")
            match.classList.remove("text-charcoal")
          }
        }
      }
    } catch (e) {
      // silently ignore — slot conflicts caught on submit
    } finally {
      if (requestController === this.slotRequestController) {
        this.slotRequestController = null
      }
    }
  }

  // Block submission until a time slot is chosen — prevents a 500 from a
  // blank starts_at reaching the server.
  validateSlot(event) {
    // Non-cancellable bookings still use this controller for editing Majoo and
    // guest details, but do not render rescheduling fields.
    if (!this.hasStartsAtTarget) return

    if (this.startsAtTarget.value) {
      if (this.hasSlotErrorTarget) this.slotErrorTarget.classList.add("hidden")
      return
    }

    event.preventDefault()
    if (this.hasSlotErrorTarget) {
      this.slotErrorTarget.classList.remove("hidden")
      this.slotErrorTarget.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  selectSlot(event) {
    const btn = event.currentTarget
    this.startsAtTarget.value = btn.dataset.startsAt
    this.clearSlotHighlight()
    btn.classList.add("bg-primary/10", "border-primary", "font-semibold")
    btn.classList.remove("text-charcoal")
    if (this.hasSlotErrorTarget) this.slotErrorTarget.classList.add("hidden")
  }

  clearSlotHighlight() {
    this.element.querySelectorAll(".dashboard-slot-btn").forEach(btn => {
      btn.classList.remove("bg-primary/10", "border-primary", "font-semibold")
      btn.classList.add("text-charcoal")
    })
  }
}
